import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Edit, Trash2, GripVertical, Circle, Square, RectangleHorizontal } from 'lucide-react';
import { Mesa, MesaForma, Guest } from '@/types';
import { computeSeatsUsed } from './mesaUtils';

interface MesaCardProps {
  mesa: Mesa;
  assignedGuests?: Guest[];
  onEdit?: (mesa: Mesa) => void;
  onDelete?: (mesa: Mesa) => void;
  onUnassignGuest?: (guestId: string) => void;
  dragHandleProps?: {
    attributes?: Record<string, unknown> | object;
    listeners?: Record<string, unknown> | object;
  };
  isDragOver?: boolean;
  variant?: 'distribucion' | 'asignaciones';
}

const formaIcon = (forma: MesaForma) => {
  switch (forma) {
    case 'REDONDA':
      return <Circle className="w-3 h-3" />;
    case 'CUADRADA':
      return <Square className="w-3 h-3" />;
    case 'RECTANGULAR':
      return <RectangleHorizontal className="w-3 h-3" />;
  }
};

const formaLabel = (forma: MesaForma) => {
  return forma === 'REDONDA' ? 'Redonda' : forma === 'CUADRADA' ? 'Cuadrada' : 'Rectangular';
};

export const MesaCard: React.FC<MesaCardProps> = ({
  mesa,
  assignedGuests = [],
  onEdit,
  onDelete,
  onUnassignGuest,
  dragHandleProps,
  isDragOver,
  variant = 'distribucion',
}) => {
  const seatsUsed = computeSeatsUsed(assignedGuests);
  const overCapacity = seatsUsed > mesa.capacidad;
  const occupancyPct = mesa.capacidad > 0
    ? Math.min(100, Math.round((seatsUsed / mesa.capacidad) * 100))
    : 0;

  return (
    <Card
      className={`transition-colors ${isDragOver ? 'ring-2 ring-cyan-500 bg-cyan-50/30' : ''} ${
        overCapacity ? 'border-red-300' : ''
      }`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {dragHandleProps && (
              <button
                type="button"
                className="cursor-grab text-gray-400 hover:text-gray-600 flex-shrink-0"
                {...dragHandleProps.attributes}
                {...dragHandleProps.listeners}
                aria-label="Reordenar mesa"
              >
                <GripVertical className="w-4 h-4" />
              </button>
            )}
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-gray-900">Mesa {mesa.numero}</span>
              </div>
              {mesa.nombre && (
                <p className="text-xs text-gray-500 truncate" title={mesa.nombre}>
                  {mesa.nombre}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(mesa)}
                aria-label="Editar mesa"
              >
                <Edit className="w-4 h-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(mesa)}
                aria-label="Eliminar mesa"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="gap-1">
            {formaIcon(mesa.forma)}
            {formaLabel(mesa.forma)}
          </Badge>
          <Badge variant={overCapacity ? 'destructive' : 'secondary'}>
            {seatsUsed}/{mesa.capacidad}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Progress
          value={occupancyPct}
          className={`h-2 mb-3 ${overCapacity ? '[&>div]:bg-red-500' : ''}`}
        />
        {variant === 'asignaciones' && (
          <div className="space-y-1 min-h-[60px]">
            {assignedGuests.length === 0 ? (
              <p className="text-xs text-gray-400 italic text-center py-2">
                Arrastra invitados aquí
              </p>
            ) : (
              assignedGuests.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center justify-between gap-1 text-sm bg-gray-50 rounded px-2 py-1"
                >
                  <span className="truncate" title={g.nombre}>
                    {g.nombre}
                    {g.numero_acompanantes > 0 && (
                      <span className="text-gray-500"> (+{g.numero_acompanantes})</span>
                    )}
                  </span>
                  {onUnassignGuest && (
                    <button
                      type="button"
                      onClick={() => onUnassignGuest(g.id)}
                      className="text-gray-400 hover:text-red-500 flex-shrink-0"
                      aria-label={`Quitar a ${g.nombre} de la mesa`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
